import { useCallback } from 'react';
import { computeTotalProgressPercent } from '../utils/formatters';

export const useDashboardActions = (setError) => {
  const handleDownloadExcel = useCallback(async (selectedProject, dataByUnit, projectList) => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    try {
      const projectName = projectList.find(p => p.id === selectedProject)?.name || 'project';
      const fileName = `${projectName}_progress_bangunan_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Create worksheet data
      const worksheetData = [
        ['BANGUNAN', 'MULAI', 'SELESAI', 'DURASI (HARI)', 'PROGRES SEBELUMNYA (%)', 'PROGRES MINGGU INI (%)', 'PROGRESS TOTAL (%)'],
        ...(dataByUnit || []).map(unit => [
          unit.moduleName,
          unit.startDate || '',
          unit.endDate || '',
          unit.durationDays || 0,
          parseFloat(unit.progressBeforeThisWeekPercentage || 0).toFixed(2),
          parseFloat(unit.progressThisWeekPercentage || 0).toFixed(2),
          computeTotalProgressPercent(unit).toFixed(2)
        ])
      ];

      // Create workbook and worksheet (simplified - in production use xlsx library)
      const blob = new Blob([JSON.stringify(worksheetData)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      setError('Failed to download Excel file');
    }
  }, [setError]);

  const handleDownloadPDF = useCallback(async (selectedProject, dataByUnit, projectList) => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    try {
      const projectName = projectList.find(p => p.id === selectedProject)?.name || 'project';
      const fileName = `${projectName}_progress_bangunan_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Create PDF content (simplified - in production use jsPDF or similar)
      const content = `
        <html>
        <head><title>Progress Bangunan - ${projectName}</title></head>
        <body>
          <h1>Progress Bangunan - ${projectName}</h1>
          <table border="1" style="border-collapse: collapse;">
            <tr>
              <th>BANGUNAN</th>
              <th>MULAI</th>
              <th>SELESAI</th>
              <th>DURASI (HARI)</th>
              <th>PROGRES SEBELUMNYA (%)</th>
              <th>PROGRES MINGGU INI (%)</th>
              <th>PROGRESS TOTAL (%)</th>
            </tr>
            ${(dataByUnit || []).map(unit => `
              <tr>
                <td>${unit.moduleName}</td>
                <td>${unit.startDate || ''}</td>
                <td>${unit.endDate || ''}</td>
                <td>${unit.durationDays || 0}</td>
                <td>${parseFloat(unit.progressBeforeThisWeekPercentage || 0).toFixed(2)}</td>
                <td>${parseFloat(unit.progressThisWeekPercentage || 0).toFixed(2)}</td>
                <td>${computeTotalProgressPercent(unit).toFixed(2)}</td>
              </tr>
            `).join('')}
          </table>
        </body>
        </html>
      `;
      
      const blob = new Blob([content], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF file');
    }
  }, [setError]);

  const handleDownloadKategoriExcel = useCallback(async (selectedProject, selectedBangunan, unitOverviewData, bangunanList) => {
    if (!selectedProject || !selectedBangunan) {
      setError('Please select a project and unit bangunan first');
      return;
    }

    try {
      const bangunanName = bangunanList.find(b => b.id === selectedBangunan)?.name || 'unit';
      const fileName = `${bangunanName}_progress_kategori_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      const worksheetData = [];
      
      unitOverviewData.tasksPerKategori.forEach(categoryGroup => {
        worksheetData.push([categoryGroup.kategori]);
        worksheetData.push(['', 'TASK NAME', 'PERIODE LALU (%)', 'PERIODE INI (%)', 'PROGRESS TOTAL (%)', 'START DATE', 'END DATE', 'DURATION (HARI)']);
        categoryGroup.task.forEach(task => {
          worksheetData.push([
            '',
            task.name,
            (task.progressPeriodeLalu || 0).toFixed(2),
            (task.progressPeriodeIni || 0).toFixed(2),
            (task.progressTotal || 0).toFixed(2),
            task.startDate || '',
            task.endDate || '',
            task.duration || 0
          ]);
        });
        worksheetData.push([]);
      });

      const blob = new Blob([JSON.stringify(worksheetData)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      setError('Failed to download Excel file');
    }
  }, [setError]);

  const handleDownloadKategoriPDF = useCallback(async (selectedProject, selectedBangunan, unitOverviewData, bangunanList) => {
    if (!selectedProject || !selectedBangunan) {
      setError('Please select a project and unit bangunan first');
      return;
    }

    try {
      const bangunanName = bangunanList.find(b => b.id === selectedBangunan)?.name || 'unit';
      const fileName = `${bangunanName}_progress_kategori_${new Date().toISOString().split('T')[0]}.pdf`;
      
      const content = `
        <html>
        <head><title>Progress Kategori - ${bangunanName}</title></head>
        <body>
          <h1>Progress Kategori - ${bangunanName}</h1>
          ${unitOverviewData.tasksPerKategori.map(categoryGroup => `
            <h2>${categoryGroup.kategori}</h2>
            <table border="1" style="border-collapse: collapse; margin-bottom: 20px;">
              <tr>
                <th>TASK NAME</th>
                <th>PERIODE LALU (%)</th>
                <th>PERIODE INI (%)</th>
                <th>PROGRESS TOTAL (%)</th>
                <th>START DATE</th>
                <th>END DATE</th>
                <th>DURATION (HARI)</th>
              </tr>
              ${categoryGroup.task.map(task => `
                <tr>
                  <td>${task.name}</td>
                  <td>${(task.progressPeriodeLalu || 0).toFixed(2)}</td>
                  <td>${(task.progressPeriodeIni || 0).toFixed(2)}</td>
                  <td>${(task.progressTotal || 0).toFixed(2)}</td>
                  <td>${task.startDate || ''}</td>
                  <td>${task.endDate || ''}</td>
                  <td>${task.duration || 0}</td>
                </tr>
              `).join('')}
            </table>
          `).join('')}
        </body>
        </html>
      `;
      
      const blob = new Blob([content], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF file');
    }
  }, [setError]);

  return {
    handleDownloadExcel,
    handleDownloadPDF,
    handleDownloadKategoriExcel,
    handleDownloadKategoriPDF
  };
};
